import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  FilledButton,
  IconButton,
  TextButton,
  InfoWidget,
} from '@ds/react-components';
import { List as ListLoader } from 'react-content-loader';
import { Typography, Dialog } from '@material-ui/core';
import moment from 'moment';
import { ReactComponent as CloseSvg } from 'assets/icons/ic-close-modal.svg';

const styles = (theme) => ({
  root: {},
  expandButton: {
    color: theme.palette.common.lightText,
  },
  dialog: {
    maxWidth: '750px',
  },
  dialogTitleContainer: {
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row',
    margin: '30px 32px 0 40px',
    justifyContent: 'space-between',
    alignContent: 'center',
  },
  closeButton: {
    margin: '0',
    color: theme.palette.common.black,
  },
  closeIcon: {
    width: '16px',
    height: '16px',
  },
  dialogContentContainer: {
    flex: '1 1 auto',
    overflowY: 'auto',
    maxHeight: '400px',
    minHeight: '40px',
    margin: '30px 36px 0 40px',
    padding: '0 14px 10px 0',
  },
  dialogText: {
    color: theme.palette.common.darkerText,
    padding: '1px 14px 1px 1px',
    margin: '10px 0',
  },
  dialogActionItemContainer: {
    flex: '0 0 auto',
    margin: '30px 36px 30px 40px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loader: {
    height: 150,
    width: '100%',
  },
});

const AdditionalInfoPreview = (props) => {
  const { classes, customerDetails, localisation, className } = props;
  const { adfitNo, notes, loadDate } = customerDetails;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const closeDialog = useCallback(() => {
    setDialogOpen(false);
  }, [setDialogOpen]);

  //Check if the string has just blank spaces.
  const isNotEmptyString = (value) => (!value ? false : !/^\s*$/g.test(value));

  const formattedNotes = useMemo(() => {
    if (!notes) return '';
    return (
      notes
        .trim()
        .split(/(?:\r|\n|\r\n)+$/gim)
        // Remove lines with empty strings in them.
        .filter(isNotEmptyString)
    );
  }, [notes]);

  return (
    <>
      <InfoWidget
        title={'Additional Information'}
        actionButton={
          isNotEmptyString(notes) ? (
            <TextButton onClick={() => setDialogOpen(true)}>
              View All
            </TextButton>
          ) : (
            <React.Fragment />
          )
        }
        isLoading={!adfitNo}
        contentLoader={<ListLoader className={classes.loader} />}
        classes={{ root: classNames(className, classes.root) }}>
        <Typography>
          {notes
            ? notes.substring(0, 190) + (notes.length > 190 ? '...' : '')
            : 'No additional information yet'}
        </Typography>
        <Typography className={classes.textGrey}>
          {loadDate
            ? 'Created ' +
              moment(loadDate).format(localisation.locales.dateTimeFormat)
            : ''}
        </Typography>
      </InfoWidget>

      <Dialog
        classes={{ paperWidthMd: classes.dialog }}
        open={isDialogOpen}
        maxWidth="md"
        scroll="paper"
        fullWidth>
        <div className={classes.dialogTitleContainer}>
          <Typography variant="h3">{'Additional Information'}</Typography>
          <IconButton
            aria-label="Close"
            variant={'none'}
            className={classes.closeButton}
            onClick={closeDialog}>
            <CloseSvg className={classes.closeIcon} />
          </IconButton>
        </div>
        <div className={classes.dialogContentContainer}>
          {formattedNotes &&
            formattedNotes.map((val, index) => (
              <Typography
                variant="body1"
                key={index}
                className={classes.dialogText}
                dangerouslySetInnerHTML={{ __html: val }}
              />
            ))}
        </div>
        <div className={classes.dialogActionItemContainer}>
          <FilledButton variant={'primary'} onClick={closeDialog}>
            {'Ok'}
          </FilledButton>
        </div>
      </Dialog>
    </>
  );
};

AdditionalInfoPreview.propTypes = {
  classes: PropTypes.object.isRequired,
  permissions: PropTypes.array.isRequired,
  className: PropTypes.string,
  localisation: PropTypes.shape({
    locales: PropTypes.shape({
      dateTimeFormat: PropTypes.string,
    }).isRequired,
  }),
};

export default withStyles(styles)(AdditionalInfoPreview);
